import React from 'react';
import { Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import PaginationComponent from '../PaginationComponent';
import { SHIFT_STATUS_COLORS, SHIFT_STATUS_LABELS } from '../../utils/cashierConstants';

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatDateTime = (value, pattern = 'dd/MM HH:mm') => {
  if (!value) return '-';
  try { return format(new Date(value), pattern); } catch { return value; }
};

const differenceClass = (value) => {
  if (value > 0) return 'text-success';
  if (value < 0) return 'text-danger';
  return 'text-muted';
};

const ShiftMonitorTable = ({
  shifts,
  loading,
  filters,
  onFilterChange,
  page,
  limit,
  totalItems,
  onPageChange,
  onViewReport,
  onForceCheckout,
}) => (
  <Card className="mb-4">
    <Card.Header>
      <div className="d-flex flex-wrap justify-content-between gap-3">
        <div>
          <h5 className="mb-1">Giám sát ca thu ngân</h5>
          <small className="text-muted">Chỉ hiển thị ca có isCashier=true</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Select
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
            style={{ minWidth: 150 }}
          >
            <option value="APPROVED">Đã duyệt</option>
            <option value="IN_PROGRESS">Đang làm</option>
            <option value="COMPLETED">Hoàn tất</option>
            <option value="PENDING">Chờ duyệt</option>
            <option value="CANCELLED">Hủy</option>
            <option value="">Tất cả</option>
          </Form.Select>
          <Form.Control
            type="date"
            value={filters.date_from}
            onChange={(e) => onFilterChange('date_from', e.target.value)}
          />
          <Form.Control
            type="date"
            value={filters.date_to}
            onChange={(e) => onFilterChange('date_to', e.target.value)}
          />
          <Form.Control
            placeholder="Tìm nhân viên..."
            value={filters.search}
            onChange={(e) => onFilterChange('search', e.target.value)}
            style={{ minWidth: 160 }}
          />
        </div>
      </div>
    </Card.Header>
    <Card.Body className="p-0">
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> Đang tải...
        </div>
      ) : shifts.length === 0 ? (
        <div className="text-center text-muted py-4">
          {filters.search || filters.status
            ? 'Không tìm thấy ca thu ngân phù hợp với bộ lọc.'
            : 'Chưa có ca thu ngân nào trong khoảng thời gian này.'}
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped hover className="mb-0">
            <thead>
              <tr>
                <th>Ca / Chi nhánh</th>
                <th>Thu ngân</th>
                <th>Đầu ca</th>
                <th>Doanh thu</th>
                <th>Phiếu chi</th>
                <th>Dự kiến</th>
                <th>Thực tế</th>
                <th>Chênh lệch</th>
                <th>Trạng thái</th>
                <th className="text-end">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((shift) => (
                <tr key={shift.id}>
                  <td>
                    <div className="fw-semibold">{shift.branchName}</div>
                    <small className="text-muted">
                      {formatDateTime(shift.startTime)} – {formatDateTime(shift.endTime)}
                    </small>
                  </td>
                  <td>{shift.employeeName}</td>
                  <td>{formatCurrency(shift.openingCash)}</td>
                  <td>{formatCurrency(shift.cashRevenue)}</td>
                  <td>{formatCurrency(shift.cashExpenses)}</td>
                  <td>{formatCurrency(shift.expectedClosing)}</td>
                  <td>{formatCurrency(shift.actualClosing)}</td>
                  <td className={differenceClass(shift.difference)}>
                    {formatCurrency(shift.difference)}
                  </td>
                  <td>
                    <Badge bg={SHIFT_STATUS_COLORS[shift.status] || 'secondary'}>
                      {SHIFT_STATUS_LABELS[shift.status] || shift.status}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex justify-content-end gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => onViewReport(shift)}>
                        Báo cáo
                      </Button>
                      {(shift.status === 'APPROVED' || shift.status === 'IN_PROGRESS') && (
                        <Button size="sm" variant="outline-danger" onClick={() => onForceCheckout(shift)}>
                          Đóng ca thay
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card.Body>
    {totalItems > limit && (
      <Card.Footer className="bg-white">
        <PaginationComponent
          totalItems={totalItems}
          itemsPerPage={limit}
          currentPage={page}
          onPageChange={onPageChange}
          loading={loading}
        />
      </Card.Footer>
    )}
  </Card>
);

export default ShiftMonitorTable;
